// pages/Sponsors.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { RiArrowRightLine, RiDownloadLine, RiFilePdfLine } from "react-icons/ri";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails";

/* ─── THEME COLORS ─────────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

/* ─── CSS ──────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}

  .t-cta-w{padding:0 clamp(20px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-buttons{display:flex;gap:16px;align-items:center;flex-wrap:wrap}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  @media (max-width: 768px) {
    .t-cta-buttons { width: 100%; justify-content: center; }
    .t-cta-btn, .t-cta-btn-outline { width: 100%; justify-content: center; }
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

const Sponsors = () => {
  const { eventId } = useSummit();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brochureInfo, setBrochureInfo] = useState(null);
  const [brochurePdfUrl, setBrochurePdfUrl] = useState("");
  const [loadingBrochure, setLoadingBrochure] = useState(false);

  useEffect(() => {
    if (eventId) {
      fetchSponsors();
      fetchBrochureInfo();
    } else {
      setLoading(false);
    }
  }, [eventId]);

  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/api/sponsors/event/${eventId}`;
      console.log(`📡 Fetching sponsors from: ${url}`);
      
      const { data } = await axios.get(url);
      console.log("📦 Sponsors data:", data);
      
      if (data.success) {
        setSponsors(data.sponsors || []);
      }
    } catch (error) {
      console.error("Error fetching sponsors:", error);
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch brochure info ──
  const fetchBrochureInfo = async () => {
    if (!eventId) return;
    try {
      setLoadingBrochure(true);
      const response = await axios.get(`${API_BASE}/api/brochure/info/${eventId}`);
      if (response.data.success) {
        setBrochureInfo(response.data);
        setBrochurePdfUrl(response.data.pdfUrl || "");
      } else {
        setBrochureInfo(null);
        setBrochurePdfUrl("");
      }
    } catch (error) {
      console.log("No brochure info found (this is normal if none uploaded)");
      setBrochureInfo(null);
      setBrochurePdfUrl("");
    } finally {
      setLoadingBrochure(false);
    }
  };

  // ── Download brochure ──
  const downloadBrochure = async () => {
    if (!eventId) {
      toast.error("No event specified");
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/api/brochure/download/${eventId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sponsorship-Brochure.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading brochure:", error);
      toast.error("Failed to download brochure. Please try again later.");
    }
  };

  const getSponsorTypeColor = (type) => {
    const colors = {
      platinum: "#E5E4E2",
      gold: "#FFD700",
      silver: "#C0C0C0",
      bronze: "#CD7F32",
      partner: "#1A56DB"
    };
    return colors[type] || "#6B8090";
  };

  const getSponsorTypeLabel = (type) => {
    const labels = {
      platinum: "Platinum Sponsor",
      gold: "Gold Sponsor",
      silver: "Silver Sponsor",
      bronze: "Bronze Sponsor",
      partner: "Partner"
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "400px" 
      }}>
        <div style={{ 
          width: 48, 
          height: 48, 
          border: "3px solid #E2F2F0", 
          borderTopColor: "#2A9D8F", 
          borderRadius: "50%", 
          animation: "spin 1s linear infinite" 
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Group sponsors by type
  const groupedSponsors = sponsors.reduce((acc, sponsor) => {
    const type = sponsor.sponsorType || 'partner';
    if (!acc[type]) acc[type] = [];
    acc[type].push(sponsor);
    return acc;
  }, {});

  const sponsorTypeOrder = ['platinum', 'gold', 'silver', 'bronze', 'partner'];

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO SECTION ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <span style={{ fontSize: 16 }}>🏆</span>
              Meetings21 · Sponsorship
            </div>
            <h1 className="t-hero-h">
              Sponsorship <em>Opportunities</em>
            </h1>
            <p className="t-hero-p">
              Partner with us to reach a global audience of researchers, scientists, and industry leaders.
              Showcase your brand and connect with the immunology community.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{sponsors.length}</span>
              <span className="t-st-l">Sponsors</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">5</span>
              <span className="t-st-l">Tiers</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">50+</span>
              <span className="t-st-l">Benefits</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ MAIN CONTENT ══════════ */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(20px,4vw,64px) 80px' }}>

        {/* Display sponsors by type */}
        {sponsors.length > 0 && (
          <section style={{ marginBottom: "60px" }}>
            <h2
              style={{
                fontSize: "32px",
                color: T.navy,
                borderBottom: `3px solid ${T.primary}`,
                paddingBottom: "12px",
                marginBottom: "36px",
                fontWeight: "700",
                letterSpacing: "-0.01em",
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Our Sponsors
            </h2>

            {sponsorTypeOrder.map(type => {
              const typeSponsors = groupedSponsors[type] || [];
              if (typeSponsors.length === 0) return null;

              return (
                <div key={type} style={{ marginBottom: "40px" }}>
                  <h3
                    style={{
                      fontSize: "22px",
                      color: T.text,
                      marginBottom: "20px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: getSponsorTypeColor(type),
                        border: "1px solid #e2e8f0",
                      }}
                    />
                    {getSponsorTypeLabel(type)}
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "24px",
                    }}
                  >
                    {typeSponsors.map((sponsor) => (
                      <div
                        key={sponsor._id}
                        className="t-card"
                        style={{
                          textAlign: "center",
                          padding: "24px",
                          border: `1px solid ${T.borderLight}`,
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow = T.shadowLg;
                          e.currentTarget.style.borderColor = getSponsorTypeColor(type);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = T.shadow;
                          e.currentTarget.style.borderColor = T.borderLight;
                        }}
                      >
                        {sponsor.logoUrl ? (
                          <img
                            src={sponsor.logoUrl}
                            alt={sponsor.name}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "80px",
                              objectFit: "contain",
                              marginBottom: "12px",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              fontSize: "40px",
                              marginBottom: "12px",
                            }}
                          >
                            🏢
                          </div>
                        )}
                        <h4
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: T.text,
                            marginBottom: "4px",
                          }}
                        >
                          {sponsor.name}
                        </h4>
                        {sponsor.url && (
                          <a
                            href={sponsor.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: "13px",
                              color: T.primary,
                              textDecoration: "none",
                              display: "inline-block",
                              marginTop: "8px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.textDecoration = "underline";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.textDecoration = "none";
                            }}
                          >
                            Visit Website →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Why Sponsor Section */}
        <section style={{ marginBottom: "70px" }}>
          <h2
            style={{
              fontSize: "32px",
              color: T.navy,
              borderBottom: `3px solid ${T.primary}`,
              paddingBottom: "12px",
              marginBottom: "28px",
              fontWeight: "700",
              letterSpacing: "-0.01em",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            Why You Should Join as Sponsor / Exhibitor?
          </h2>

          <p
            style={{
              fontSize: "17px",
              marginBottom: "20px",
              color: T.textMid,
              lineHeight: "1.7",
            }}
          >
            We are pleased to invite your participation as Sponsor or Exhibitor in{" "}
            <strong style={{ color: T.primary }}>Meetings21</strong>.
          </p>

          <p
            style={{
              fontSize: "16px",
              color: T.textMuted,
              lineHeight: "1.7",
              marginBottom: "20px",
            }}
          >
            The extensive 3-day program will bring together the global community
            of researchers, scientists, academicians, industry leaders, and
            government representatives to share knowledge and build valuable
            professional networks.
          </p>

          <p
            style={{
              fontSize: "16px",
              color: T.textMuted,
              lineHeight: "1.7",
              marginBottom: "32px",
            }}
          >
            Congress Partners are offered the valuable opportunity to associate
            their brand and profile with this significant event, demonstrating
            their commitment, innovation, and leadership in the field.
          </p>

          <h3
            style={{
              fontSize: "22px",
              color: T.text,
              marginBottom: "20px",
              fontWeight: "600",
            }}
          >
            6 Key Benefits of Sponsoring:
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "16px",
            }}
          >
            {[
              "Position your product as a market leader",
              "Maximum brand exposure to 1,000+ attendees",
              "Global Networking with industry pioneers",
              "High-quality Lead Generation opportunities",
              "Connect with the global immunology community",
              "Valuable Business Insights & market trends",
            ].map((benefit, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 16px",
                  background: T.offWhite,
                  borderRadius: "12px",
                  border: `1px solid ${T.borderLight}`,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = T.primary;
                  e.currentTarget.style.boxShadow = T.shadow;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = T.borderLight;
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    background: T.gradPrimary,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: "700",
                    fontSize: "14px",
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>
                <span style={{ fontSize: "15px", fontWeight: "500", color: T.text }}>
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ─── BROCHURE DOWNLOAD SECTION ─── */}
        <div style={{ textAlign: "center", margin: "48px 0" }}>
          {brochurePdfUrl ? (
            <button
              onClick={downloadBrochure}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                padding: "16px 40px",
                background: T.gradGold,
                color: T.navyDeep,
                fontWeight: "700",
                borderRadius: "40px",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: T.shadowGold,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = T.shadowLg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = T.shadowGold;
              }}
            >
              <RiDownloadLine size={20} /> Download Sponsorship Brochure
            </button>
          ) : (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                padding: "16px 32px",
                background: T.offWhite,
                border: `2px dashed ${T.border}`,
                borderRadius: "40px",
                color: T.textMuted,
              }}
            >
              <RiFilePdfLine size={24} style={{ color: T.primary }} />
              <span>Brochure coming soon</span>
            </div>
          )}
        </div>

        {/* Exhibition Info */}
        <section
          style={{
            background: `linear-gradient(135deg, ${T.softTeal} 0%, ${T.white} 100%)`,
            padding: "32px 36px",
            borderRadius: "20px",
            marginBottom: "60px",
            border: `1px solid ${T.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <span style={{ fontSize: "28px" }}>🏛️</span>
            <h3
              style={{
                fontSize: "24px",
                color: T.navy,
                fontWeight: "700",
                margin: 0,
                fontFamily: "'Playfair Display', serif",
              }}
            >
              Exhibition
            </h3>
          </div>
          <p
            style={{
              fontSize: "16px",
              color: T.textMid,
              marginBottom: "8px",
            }}
          >
            <strong style={{ color: T.primary }}>Exhibition Time:</strong>{" "}
            09:00 - 17:00 on all conference days
          </p>
          <p style={{ fontSize: "14px", color: T.textMuted, marginTop: "12px" }}>
            * Setup available from 08:00 on the first day | Dismantling after 17:00 on the final day
          </p>
        </section>

        {/* Other Sponsorship Packages */}
        <section>
          <h2
            style={{
              fontSize: "32px",
              color: T.navy,
              marginBottom: "36px",
              fontWeight: "700",
              letterSpacing: "-0.01em",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            <span style={{ fontSize: "32px" }}>🎯</span>
            Other Sponsorship Packages
          </h2>

          <div
            style={{
              display: "grid",
              gap: "28px",
              gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
            }}
          >
            {/* Conference Bag */}
            <div
              className="t-card"
              style={{
                padding: "28px",
                border: `1px solid ${T.borderLight}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = T.shadowLg;
                e.currentTarget.style.borderColor = T.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = T.shadow;
                e.currentTarget.style.borderColor = T.borderLight;
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "16px",
                }}
              >
                🎒
              </div>
              <h3 style={{ color: T.text, marginBottom: "12px", fontSize: "20px", fontWeight: "700" }}>
                Conference Bag
              </h3>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: T.primary,
                  marginBottom: "20px",
                }}
              >
                $2,000
              </div>
              <ul
                style={{
                  paddingLeft: "20px",
                  fontSize: "14px",
                  color: T.textMuted,
                  lineHeight: "1.8",
                }}
              >
                <li>Sponsor's logo on conference bags distributed to all attendees</li>
                <li>Logo placement on event website and app</li>
                <li>Standard sponsorship benefits package</li>
              </ul>
            </div>

            {/* Notebook / Pen / USB */}
            <div
              className="t-card"
              style={{
                padding: "28px",
                border: `1px solid ${T.borderLight}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = T.shadowLg;
                e.currentTarget.style.borderColor = T.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = T.shadow;
                e.currentTarget.style.borderColor = T.borderLight;
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "16px",
                }}
              >
                ✍️
              </div>
              <h3 style={{ color: T.text, marginBottom: "12px", fontSize: "20px", fontWeight: "700" }}>
                Meeting Notebook / Pen / USB
              </h3>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: T.primary,
                  marginBottom: "20px",
                }}
              >
                $2,000
              </div>
              <ul
                style={{
                  paddingLeft: "20px",
                  fontSize: "14px",
                  color: T.textMuted,
                  lineHeight: "1.8",
                }}
              >
                <li>Sponsor's branding on meeting notebooks / pens / USB drives</li>
                <li>Logo placement on event website</li>
                <li>Standard sponsorship benefits package</li>
              </ul>
            </div>

            {/* Best Poster */}
            <div
              className="t-card"
              style={{
                padding: "28px",
                border: `1px solid ${T.borderLight}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = T.shadowLg;
                e.currentTarget.style.borderColor = T.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = T.shadow;
                e.currentTarget.style.borderColor = T.borderLight;
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "16px",
                }}
              >
                🏆
              </div>
              <h3 style={{ color: T.text, marginBottom: "12px", fontSize: "20px", fontWeight: "700" }}>
                Best Poster Presentation
              </h3>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: T.primary,
                  marginBottom: "8px",
                }}
              >
                $900
              </div>
              <p style={{ fontSize: "13px", color: T.textMuted, marginBottom: "20px" }}>
                ($300 × 3 winners)
              </p>
              <ul
                style={{
                  paddingLeft: "20px",
                  fontSize: "14px",
                  color: T.textMuted,
                  lineHeight: "1.8",
                }}
              >
                <li>Sponsor's logo on Conference website as award sponsor</li>
                <li>Sponsor's branding on winner certificates</li>
                <li>Recognition during award ceremony</li>
              </ul>
            </div>

            {/* Best Oral */}
            <div
              className="t-card"
              style={{
                padding: "28px",
                border: `1px solid ${T.borderLight}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = T.shadowLg;
                e.currentTarget.style.borderColor = T.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = T.shadow;
                e.currentTarget.style.borderColor = T.borderLight;
              }}
            >
              <div
                style={{
                  fontSize: "40px",
                  marginBottom: "16px",
                }}
              >
                🎙️
              </div>
              <h3 style={{ color: T.text, marginBottom: "12px", fontSize: "20px", fontWeight: "700" }}>
                Best Oral Presentation
              </h3>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "800",
                  color: T.primary,
                  marginBottom: "8px",
                }}
              >
                $1,000
              </div>
              <p style={{ fontSize: "13px", color: T.textMuted, marginBottom: "20px" }}>
                ($500 × 2 winners)
              </p>
              <ul
                style={{
                  paddingLeft: "20px",
                  fontSize: "14px",
                  color: T.textMuted,
                  lineHeight: "1.8",
                }}
              >
                <li>Sponsor's logo on Conference website as award sponsor</li>
                <li>Sponsor's branding on winner certificates</li>
                <li>Recognition during award ceremony</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* ── CTA SECTION ── */}
      <div className="t-cta-w">
        <motion.div 
          className="t-cta" 
          initial={{ opacity: 0, y: 18 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.4 }} 
          viewport={{ once: true }}
        >
          <div>
            <div className="t-cta-tag">Interested in Sponsoring?</div>
            <h2 className="t-cta-h">Let's <span style={{ color: T.accentGold }}>Partner</span> Together</h2>
            <p className="t-cta-p">Contact us today to discuss customized sponsorship packages tailored to your goals.</p>
          </div>
          <div className="t-cta-buttons">
            <Link 
              to={eventId ? `/contact?eventId=${eventId}` : "/contact"}  
              className="t-cta-btn"
            >
              Contact Sponsorship Team <RiArrowRightLine size={15} />
            </Link>
            <Link 
              to={eventId ? `/about?eventId=${eventId}` : "/about"}  
              className="t-cta-btn-outline"
            >
              Learn About Us <RiArrowRightLine size={15} />
            </Link>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default Sponsors;